import React, { KeyboardEventHandler } from 'react';
import * as Rally from '@rally/ui-themes/dist/web/rally/tokens.es6';
import cx from 'classnames';
import Select, { Props as ReactSelectProps, components } from 'react-select';
import { useDebounce } from 'rooks';
import styled from 'styled-components';
import { isServerEnvironment } from '@pass-frontend/features/configuration/data-provider';
import { getRDSColor } from '@pass-frontend/utility/style-helpers';

//NOTE: Fix for PASS-3369
const menuPortalTarget = !isServerEnvironment()
  ? document.getElementById('__next')
  : null;

type SelectPropsGeneric<OptionType> = ReactSelectProps<OptionType>;

// interface TypeaheadProps {
//   onChange: SelectProps['onChange'];
//   value: SelectProps['value'];
//   passthruProps?: Partial<SelectProps>;
//   debouncingTime?: number;
// }

const StyledMenuContainer = styled.div`
  &.typeahead--no-options {
    .typeahead__menu {
      display: none;
    }
  }
  .typeahead__menu-list {
    padding-top: 0;
    padding-bottom: 0;

    .typeahead__option {
      color: ${getRDSColor('primaryColor')} !important;
      font-size: ${Rally.RDS_FONT_SIZE_14};
      font-weight: ${Rally.RDS_FONT_WEIGHT_BOLD};
      cursor: pointer;

      &.typeahead__option--is-selected,
      &.typeahead__option--is-focused {
        background-color: ${Rally.RDS_COLOR_NEUTRAL_95};
      }
    }
  }
`;

interface MenuPortalProps {
  isEmpty?: boolean;
  children: React.ReactNode;
  [x: string]: any;
}

const MenuPortalContainer = ({
  isEmpty,
  children,
  ...selectMenuProps
}: any) => (
  <StyledMenuContainer
    className={cx({
      'typeahead--no-options': isEmpty,
    })}
  >
    <components.Menu {...selectMenuProps}>{children}</components.Menu>
  </StyledMenuContainer>
);

// NOTE: themes react-select to match RDS
const StyledTypeahead = styled(Select)`
  .typeahead__control {
    cursor: text;
    font-size: ${Rally.RDS_FONT_SIZE_14};
    box-shadow: none;
    color: ${Rally.RDS_COLOR_NEUTRAL_30};

    &.typeahead__control--is-focused {
      border-color: ${Rally.RDS_COLOR_NEUTRAL_30};
    }
    .typeahead__value-container {
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }

  .typeahead__indicators {
    display: none;
  }

  // hack around an weird menu showing issue.
  &.typeahead--error {
    .typeahead__control {
      border-color: ${getRDSColor('errorTextDangerColor')};
    }
  }
`;

/** NOTE: the `classNamePrefix` prop allows us to prefix a classname to all
 *    subcomponents AsyncSelect renders, and they just happen to name their
 *    classNames in BEM pattern, thus allowing us to re-theme it using the same pattern.
 */
export function TypeaheadPortal(props: any) {
  const {
    onChange,
    value,
    passthruProps: { onInputChange, ...restPassthru },
    debouncingTime,
  } = props;

  const debouncedInputChange = useDebounce(
    onInputChange,
    debouncingTime ?? 300,
  );
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    switch (event.key) {
      case 'Enter':
        // TODO: Submit form
        // Hides isMenuOpen
        // When no options return
        if (!restPassthru?.options?.length) {
          // TODO: handle error in this scenario
          console.error('Error in Typeahead, no option is available');
        }
        break;
    }
  };

  return (
    <StyledTypeahead
      className={cx('typeahead', {
        'typeahead--error': restPassthru?.shouldShowErrorMessage,
      })}
      classNamePrefix="typeahead"
      placeholder="Street Address, City & State, Zip Code"
      onInputChange={debouncedInputChange}
      // NOTE: this prop needs to be `null` or react-select will filter out options by input, so fuzzy matches will not display
      filterOption={null}
      defaultOptions={false}
      {...restPassthru}
      components={{
        LoadingIndicator: () => null,
        Menu: ({ children, ...selectMenuProps }: MenuPortalProps) => (
          <MenuPortalContainer
            {...selectMenuProps}
            children={children as React.ReactNode}
            isEmpty={!restPassthru?.options?.length}
          />
        ),
      }}
      loadingMessage={() => null}
      onKeyDown={handleKeyDown}
      onChange={onChange}
      value={value}
      controlShouldRenderValue={true}
      menuPortalTarget={menuPortalTarget}
    />
  );
}

export default TypeaheadPortal;
